import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { TypographySize } from 'mm-theme-configuration/dist/consts';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { BaseUnit } from '../../../../../theming/baseUnitDefinition';
import { useTheme } from '../../../../../theming/useTheme';
import { LinkList } from './LinkList';
import { NavigationLink } from '../primaryHeader/PrimaryHeader';
import { SubMenuReducerProps } from './subMenuReducer';
import { ErrorBoundary } from '../../../../errorHandling/ErrorBoundary';
import { RegisterLinkWithSubMenu } from './linkWithSubHeader.utils';

interface FixedNavProps {
  rowHeight: number;
  navHeightLarge: number;
  ulRef: React.RefObject<HTMLUListElement>;
  links: NavigationLink[] | null;
  pagePath: string;
  activeStateColor: string;
  hoverStateColor: string;
  idleColor: string;
  currentLinkColor: string;
  linkFontSizeLarge: TypographySize;
  linkFontSizeMedium: TypographySize;
  linkFontSizeSmall: TypographySize;
  linkDropdownIconColor: string;
  subMenuState: SubMenuReducerProps;
  indexOfFirstInvisibleLink: number;
  linkBackgroundColor: string;
  registerLinkWithSubMenu: RegisterLinkWithSubMenu;
}

const getLinkBackgroundOffset = (subMenuState: SubMenuReducerProps) => {
  if (subMenuState.element && subMenuState.index >= 0) {
    const offset = subMenuState.element.getBoundingClientRect().left;
    return offset;
  }

  return 0;
};

const getStyles = (navHeightLarge: number, baseUnit: BaseUnit, linkBackgroundColor: string, rowHeight: number, subMenuState: SubMenuReducerProps) => {
  const backgroundWidth = subMenuState.element ? subMenuState.element.getBoundingClientRect().width : 0;
  const elementOffset = getLinkBackgroundOffset(subMenuState);

  return StyleSheet.create({
    fixedNav: {
      height: `${navHeightLarge}px`,
      [MEDIA_BREAKPOINTS.large]: {
        display: 'block',
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none',
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none',
      },
    },
    navHiddenLayer: {
      overflow: 'hidden',
      height: `${navHeightLarge}px`,
    },
    menuContainer: {
      position: 'relative',
    },
    fixedUl: {
      display: 'inline',
      marginLeft: `${baseUnit}px`,
      padding: 0,
    },
    menuWrapper: {
      position: 'relative',
    },
    linkBackground: {
      position: 'absolute',
      display: elementOffset === 0 ? 'none' : 'block',
      backgroundColor: linkBackgroundColor,
      height: `${rowHeight * baseUnit}px`,
      top: 0,
      left: `${elementOffset - baseUnit / 2}px`,
      width: `${backgroundWidth + baseUnit}px`,
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none',
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none',
      },
    },
  });
};

export const FixedNav: React.FunctionComponent<FixedNavProps> = props => {
  const { baseUnit } = useTheme();
  const { navHeightLarge, ulRef, links, pagePath, activeStateColor, hoverStateColor, idleColor, rowHeight, linkFontSizeLarge, linkFontSizeMedium, linkFontSizeSmall, linkDropdownIconColor, subMenuState, indexOfFirstInvisibleLink, currentLinkColor, linkBackgroundColor, registerLinkWithSubMenu } = props;

  const linkColors = {
    activeStateColor, hoverStateColor, idleColor, currentLinkColor,
  };
  const styles = getStyles(navHeightLarge, baseUnit, linkBackgroundColor, rowHeight, subMenuState);

  const fontStyles = { linkFontSizeLarge, linkFontSizeMedium, linkFontSizeSmall };

  return (
    <ErrorBoundary>
      <div className={css(styles.linkBackground)} />
      <nav className={css(styles.fixedNav)}>
        <div className={css(styles.navHiddenLayer)}>
          <ul className={css(styles.fixedUl)} ref={ulRef}>
            {
              links && (
                <LinkList
                  links={links}
                  pagePath={pagePath}
                  {...linkColors}
                  linkDropdownIconColor={linkDropdownIconColor}
                  {...fontStyles}
                  indexOfFirstInvisibleLink={indexOfFirstInvisibleLink}
                  openLinkIndex={subMenuState.index}
                  registerLinkWithSubMenu={registerLinkWithSubMenu}
                />
              )
            }
          </ul>
        </div>
      </nav>
    </ErrorBoundary>
  );
};
